import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface BreadcrumbContextValue {
  /** 動的タイトルを設定 */
  setDynamicTitle: (key: string, title: string) => void;
  /** 動的タイトルを取得 */
  getDynamicTitle: (key: string) => string | undefined;
  /** 動的タイトルをクリア */
  clearDynamicTitle: (key: string) => void;
  /** 全ての動的タイトルをクリア */
  clearAllDynamicTitles: () => void;
  /** 動的タイトルのマップ */
  dynamicTitles: Record<string, string>;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

interface BreadcrumbProviderProps {
  children: ReactNode;
}

/**
 * パンくずリストの動的タイトルを管理するプロバイダー
 *
 * 使用例:
 * ```tsx
 * // 親コンポーネント
 * <BreadcrumbProvider>
 *   <App />
 * </BreadcrumbProvider>
 *
 * // 子コンポーネント（詳細ページなど）
 * const { setDynamicTitle } = useBreadcrumb();
 * useEffect(() => {
 *   setDynamicTitle('equipment', equipment.name);
 * }, [equipment.name]);
 * ```
 */
export function BreadcrumbProvider({ children }: BreadcrumbProviderProps) {
  const [dynamicTitles, setDynamicTitles] = useState<Record<string, string>>({});

  const setDynamicTitle = useCallback((key: string, title: string) => {
    setDynamicTitles((prev) => ({
      ...prev,
      [key]: title,
    }));
  }, []);

  const getDynamicTitle = useCallback(
    (key: string) => dynamicTitles[key],
    [dynamicTitles]
  );

  const clearDynamicTitle = useCallback((key: string) => {
    setDynamicTitles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearAllDynamicTitles = useCallback(() => {
    setDynamicTitles({});
  }, []);

  return (
    <BreadcrumbContext.Provider
      value={{
        setDynamicTitle,
        getDynamicTitle,
        clearDynamicTitle,
        clearAllDynamicTitles,
        dynamicTitles,
      }}
    >
      {children}
    </BreadcrumbContext.Provider>
  );
}

/**
 * パンくずリストコンテキストを使用するフック
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBreadcrumbContext(): BreadcrumbContextValue {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider');
  }
  return context;
}

/**
 * コンテキストがあれば使用、なければデフォルト値を返すフック
 * レイアウト外でも使用可能にするため
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useBreadcrumbContextSafe(): BreadcrumbContextValue {
  const context = useContext(BreadcrumbContext);

  // コンテキストがない場合のデフォルト実装
  const defaultValue: BreadcrumbContextValue = {
    setDynamicTitle: () => {},
    getDynamicTitle: () => undefined,
    clearDynamicTitle: () => {},
    clearAllDynamicTitles: () => {},
    dynamicTitles: {},
  };

  return context || defaultValue;
}
